import {
  ActionIcon,
  Avatar,
  Button,
  Flex,
  Group,
  Header as MantineHeader,
  Menu,
  Switch,
  Text,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core'
import {
  useActiveAuthProvider,
  useGetIdentity,
  useLogout,
} from '@refinedev/core'
import { RefineThemedLayoutV2HeaderProps } from '@refinedev/mantine'
import React, { ReactNode, useEffect, useState } from 'react'
import api from 'src/utils/Api'
import ImodalWarning from 'src/interfaces/ImodalWarning'
import { ModalWarning } from '../modalWarning'
import {
  IconAbc,
  IconAdjustments,
  IconArrowsLeftRight,
  IconCalendarDollar,
  IconChartBar,
  IconClipboardList,
  IconCurrencyReal,
  IconLogout,
  IconMoon,
  IconReceipt,
  IconReportMoney,
  IconSun,
  IconUsers,
} from '@tabler/icons-react'
import ModalOperacional from '../modalOperacional'
import { findImageByPath, openByteArrayNewTab } from 'src/utils/Files'
import IDocuments from 'src/interfaces/IDocuments'
import ModalRelatorio from '../modalRelatorio'
import IModalRelatorio from 'src/interfaces/IModalRelatorio'
import {
  REPORTS_ABC,
  REPORTS_ABC_CLIENTE,
  REPORTS_CAIXA_PREVISTO_REALIZADO,
  REPORTS_COMPRAS_BY_PERIODO,
  REPORTS_CONTAS_PAGAR,
  REPORTS_CONTAS_RECEBER,
  REPORTS_DRE,
  REPORTS_RFM,
  REPORTS_VENDAS_BY_PERIODO,
} from 'src/utils/Routes'
import { StatusEnum } from 'src/enums/StatusEnum'
import { useValidatePermission } from 'src/utils/ValidatePermissionsUser'
import { ErrorNotification } from '../notification/errorNotification'
import { MESSAGE_ERROR_SERVIDOR } from 'src/utils/Constants'
import { SelectItem } from '@mantine/core'
import ModalRelatorioDre from '../modalRelatorio DRE'
import { MesEnum } from 'src/enums/MesEnum'
import { useMediaQuery } from '@mantine/hooks'
interface iUserIdentity {
  role?: string
  nome?: string
  pathImage?: string
  permissions?: string
  login?: string
}
interface MenuConfig {
  group?: string
  label: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
  icon?: ReactNode
  disabled?: boolean
}
const MesLabel: Record<MesEnum, string> = {
  [MesEnum.JANEIRO]: 'Janeiro',
  [MesEnum.FEVEREIRO]: 'Fevereiro',
  [MesEnum.MARCO]: 'Março',
  [MesEnum.ABRIL]: 'Abril',
  [MesEnum.MAIO]: 'Maio',
  [MesEnum.JUNHO]: 'Junho',
  [MesEnum.JULHO]: 'Julho',
  [MesEnum.AGOSTO]: 'Agosto',
  [MesEnum.SETEMBRO]: 'Setembro',
  [MesEnum.OUTUBRO]: 'Outubro',
  [MesEnum.NOVEMBRO]: 'Novembro',
  [MesEnum.DEZEMBRO]: 'Dezembro',
}

export const Header: React.FC<RefineThemedLayoutV2HeaderProps> = () => {
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const { data: identity } = useGetIdentity<iUserIdentity>()
  const mesesSelect: SelectItem[] = Object.values(MesEnum).map(mes => ({
    value: mes,
    label: MesLabel[mes],
  }))
  const canShowAbc = useValidatePermission('abc_rel')
  const canShowAbcCliente = useValidatePermission('abc_cli_rel')
  const canShowCaixa = useValidatePermission('cxa_pre_rel')
  const canShowDre = useValidatePermission('dre_rel')
  const canShowRfm = useValidatePermission('rfm_rel')
  const canShowVendas = useValidatePermission('vnd_rel')
  const canShowCompras = useValidatePermission('com_rel')
  const canShowContasPagar = useValidatePermission('cnp_rel')
  const canShowContasReceber = useValidatePermission('cnr_rel')
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const [image, setImage] = useState<string>('')
  const authProvider = useActiveAuthProvider()
  const [openModalOperacional, setOpenModalOperacional] =
    useState<boolean>(false)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [openModalRelatorio, setOpenModalRelatorio] = useState<boolean>(false)
  const [openModalRelatorioDre, setOpenModalRelatorioDre] =
    useState<boolean>(false)
  const { mutate: mutateLogout } = useLogout({
    v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
  })
  const menu: MenuConfig[] = [
    {
      group: 'Estratégico',
      disabled: !canShowCaixa,
      onClick: () => {
        setModalConfigRelatorio({
          route: REPORTS_CAIXA_PREVISTO_REALIZADO,
        })
        setOpenModalRelatorio(true)
      },
      label: 'Caixa previsto x realizado',
      icon: <IconArrowsLeftRight size={18} stroke={1.5} color="#16a34a" />,
    },
    {
      disabled: !canShowDre,
      onClick: () => {
        setModalConfigRelatorio({
          route: REPORTS_DRE,
          listMes: mesesSelect,
        })
        setOpenModalRelatorioDre(true)
      },
      label: 'Demonstração de Resultados do Exercício (DRE)',
      icon: <IconReportMoney size={18} stroke={1.5} color="#18940d" />,
    },
    {
      disabled: !canShowRfm,
      onClick: () => {
        printRel(REPORTS_RFM)
      },
      label: 'Inteligência de Clientes (RFM)',
      icon: <IconUsers size={18} stroke={1.5} color="#0d9488" />,
    },
    {
      disabled: !canShowAbcCliente,
      onClick: () => {
        setModalConfigRelatorio({
          route: REPORTS_ABC_CLIENTE,
          listStatus: [StatusEnum.F, StatusEnum.P, StatusEnum.C],
        })
        setOpenModalRelatorio(true)
      },
      label: 'Curva ABC – Clientes',
      icon: <IconAbc size={18} stroke={1.5} color="#0d9488" />,
    },
    {
      disabled: !canShowAbc,
      onClick: () => {
        setModalConfigRelatorio({
          route: REPORTS_ABC,
          listStatus: [StatusEnum.F, StatusEnum.P, StatusEnum.C],
        })
        setOpenModalRelatorio(true)
      },
      label: 'Curva ABC – Produtos',
      icon: <IconAbc size={18} stroke={1.5} color="#4f46e5" />,
    },
    {
      group: 'Movimentação',
      disabled: !canShowVendas,
      onClick: () => {
        setModalConfigRelatorio({
          route: REPORTS_VENDAS_BY_PERIODO,
          listStatus: [StatusEnum.F, StatusEnum.P, StatusEnum.C],
        })
        setOpenModalRelatorio(true)
      },
      label: 'Vendas',
      icon: <IconCurrencyReal size={18} stroke={1.5} color="#0891b2" />,
    },
    {
      disabled: !canShowCompras,
      onClick: () => {
        setModalConfigRelatorio({
          route: REPORTS_COMPRAS_BY_PERIODO,
          listStatus: [StatusEnum.F, StatusEnum.P, StatusEnum.C],
        })
        setOpenModalRelatorio(true)
      },
      label: 'Compras',
      icon: <IconClipboardList size={18} stroke={1.5} color="#d97706" />,
    },
    {
      group: 'Financeiro',
      disabled: !canShowContasReceber,
      onClick: () => {
        setModalConfigRelatorio({
          route: REPORTS_CONTAS_RECEBER,
          listStatus: [
            StatusEnum.P,
            StatusEnum.AT,
            StatusEnum.PG,
            StatusEnum.PR,
            StatusEnum.S,
          ],
        })
        setOpenModalRelatorio(true)
      },
      label: 'Contas a receber',
      icon: <IconReceipt size={18} stroke={1.5} color="#16a34a" />,
    },
    {
      disabled: !canShowContasPagar,
      onClick: () => {
        setModalConfigRelatorio({
          route: REPORTS_CONTAS_PAGAR,
          listStatus: [
            StatusEnum.P,
            StatusEnum.AT,
            StatusEnum.PG,
            StatusEnum.PR,
          ],
        })
        setOpenModalRelatorio(true)
      },
      label: 'Contas a pagar',
      icon: <IconCalendarDollar size={18} stroke={1.5} color="#e11d48" />,
    },
  ]
  const printRel = async (route: string) => {
    try {
      const r = await api.post(route, null, {
        responseType: 'blob',
      })

      openByteArrayNewTab(r.data, 'file')
    } catch (error) {
      ErrorNotification({ message: MESSAGE_ERROR_SERVIDOR })
    }
  }
  const [modalConfig, setModalConfig] = useState<ImodalWarning>({
    titleWarning: '',
    descriptionWarning: '',
    confirm: () => {},
  })
  const [modaConfigRelatorio, setModalConfigRelatorio] =
    useState<IModalRelatorio>({
      route: '',
      listStatus: [],
      listMes: [],
    })
  const openModaLogout = () => {
    setModalConfig({
      confirm: () => handleLogout(),
      titleWarning: 'Tem certeza ?',
      descriptionWarning:
        'Ao confirmar, o usuário será desconectado e terá que realizar o login novamente para continuar.',
    })
    setOpenModal(true)
  }

  const modalOperacional = () => {
    setOpenModalOperacional(true)
  }

  const handleLogout = () => {
    mutateLogout()
  }
  const closeModal = (e: boolean) => {
    setOpenModal(e)
    setOpenModalRelatorio(e)
    setOpenModalRelatorioDre(e)
    setOpenModalOperacional(e)
  }
  useEffect(() => {
    getImageUser()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity?.pathImage])
  const getImageUser = async () => {
    const document: IDocuments = {
      id: null,
      nome: '',
      path: identity?.pathImage ?? '',
      size: '',
    }
    if (document.path != '' && document.path != null) {
      const imageUrl = await findImageByPath(
        document,
        'Erro ao buscar a imagem:'
      )
      setImage(imageUrl!)
    }
  }

  return (
    <MantineHeader
      height={60}
      px="md"
      py={6}
      withBorder={false}
      mb="0.5rem"
      sx={theme => ({
        borderRadius: 12,
        boxShadow:
          theme.colorScheme === 'dark'
            ? '0 4px 12px rgba(0,0,0,0.5)'
            : '0 4px 12px rgba(0,0,0,0.12)',
      })}
    >
      <Flex
        align="center"
        justify="space-between"
        sx={{
          height: '100%',
        }}
      >
        <Menu
          shadow="md"
          withArrow
          position="bottom-start"
          width={'260'}
          withinPortal
          transitionProps={{ transition: 'scale-y', duration: 150 }}
        >
          <Menu.Target>
            <Button
              variant="subtle"
              color="gray"
              size="sm"
              leftIcon={
                <IconChartBar
                  color={colorScheme == 'dark' ? '#fff' : '#1b6dbb'}
                  size={18}
                  stroke={1.5}
                />
              }
              styles={{
                root: {
                  fontWeight: 500,
                  color: colorScheme == 'dark' ? '#fff' : '#1b6dbb',
                },
              }}
            >
              Relatórios
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {menu.map((button, index) => (
              <React.Fragment key={index}>
                {button.group && (
                  <Menu.Label
                    sx={{
                      fontWeight: 700,
                      fontSize: 10,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: '#64748b',
                      marginTop: index !== 0 ? 8 : 0,
                    }}
                  >
                    {button.group}
                  </Menu.Label>
                )}

                <Menu.Item
                  disabled={button.disabled}
                  onClick={button.onClick}
                  icon={button.icon}
                  sx={{
                    borderRadius: 6,
                    marginBottom: 2,
                    '&:hover': {
                      backgroundColor: '#f1f5f9',
                    },
                  }}
                >
                  {button.label}
                </Menu.Item>
              </React.Fragment>
            ))}
          </Menu.Dropdown>
        </Menu>
        {isDesktop && (
          <Group>
            <Tooltip label={'Configuração'}>
              <ActionIcon
                onClick={() => modalOperacional()}
                disabled={!identity?.permissions?.includes('ope_l')}
                variant="light"
                color="blue"
                radius={'lg'}
                size="lg"
              >
                <IconAdjustments />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={'Sair'}>
              <ActionIcon
                variant="light"
                onClick={() => openModaLogout()}
                radius={'lg'}
                size="lg"
                color="red"
              >
                <IconLogout />
              </ActionIcon>
            </Tooltip>
            <Flex align={'center'} justify={'center'}>
              <Avatar
                src={image}
                size="lg"
                radius="xl"
                sx={theme => ({
                  cursor: 'pointer',
                  border: `2px solid ${theme.colors.blue[6]}`,
                  transition: 'transform 150ms ease, box-shadow 150ms ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 0 0 4px rgba(34,139,230,0.25)',
                  },
                })}
              />
              <Flex direction={'column'} ml={'xs'} align={'center'}>
                <Flex align={'start'}>
                  <Tooltip label={identity?.nome}>
                    <Text
                      fz="sm"
                      fw={600}
                      lineClamp={1}
                      maw={120}
                      sx={{ cursor: 'pointer' }}
                    >
                      {identity?.nome}
                    </Text>
                  </Tooltip>
                </Flex>
                <Flex align={'center'} gap={'md'}>
                  <Switch
                    checked={colorScheme === 'dark'}
                    onChange={() => toggleColorScheme()}
                    size="md"
                    radius="xl"
                    onLabel={<IconMoon size={14} />}
                    offLabel={<IconSun size={14} />}
                    styles={{
                      track: {
                        cursor: 'pointer',
                      },
                    }}
                  />
                  <Text fz="xs" c="dimmed" align="end">
                    {identity?.role}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Group>
        )}
        {!isDesktop && (
          <Group>
            <Tooltip label={'Sair'}>
              <ActionIcon
                variant="light"
                onClick={() => openModaLogout()}
                radius={'lg'}
                size="lg"
                color="red"
              >
                <IconLogout />
              </ActionIcon>
            </Tooltip>
          </Group>
        )}
      </Flex>
      <ModalWarning
        confirm={modalConfig.confirm}
        titleWarning={modalConfig.titleWarning}
        descriptionWarning={modalConfig.descriptionWarning}
        openModal={openModal}
        closeModal={closeModal}
      />
      <ModalOperacional
        openModal={openModalOperacional}
        onClose={e => closeModal(e)}
      />
      <ModalRelatorio
        route={modaConfigRelatorio.route}
        listStatus={modaConfigRelatorio.listStatus}
        openModal={openModalRelatorio}
        closeModal={e => closeModal(e)}
      />
      <ModalRelatorioDre
        route={modaConfigRelatorio.route}
        listMes={modaConfigRelatorio.listMes!}
        openModal={openModalRelatorioDre}
        closeModal={e => closeModal(e)}
      />
    </MantineHeader>
  )
}
